// Navbar + mobile menu. The logo and menu (titles, dropdown links) are edited in the admin
// (Website → Navbar, footer & offices) and come from useGlobalContent(); empty fields fall
// back to src/data/globalDefaults.js. Portal/admin links and the controls stay in code.
import React, { useState, useEffect } from "react";
import "./Navbar.css";
import "../../components/Logo/Logo.css";
import navLogo from "../../assets/global/navbar/vision-security-logo.webp";
import { localizePath } from "../../data/serviceSlugsFr";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, Lock, LogIn, Briefcase, UserRound } from "lucide-react";
import Sidebar from "../../components/Sidebar";
import PhoneModal from "../../components/PhoneModal";
import useNavbar from "../../hooks/useNavbar";
import useDarkMode from "../../hooks/useDarkMode";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie"; // Import Cookies for cookie handling
import { useGlobalContent } from "../../components/GlobalContent";
import { E, useEd } from "../../components/Editable";

const CLIENT_PORTAL_URL = "https://autodialer-phi.vercel.app/portal/login";
const EMPLOYEE_PORTAL_URL = "https://autodialer-phi.vercel.app/field/login";

/* ===== Locale helpers ===== */
const getLangFromPath = (pathname) => (pathname.startsWith("/fr") ? "fr" : "en");

// Shared helper also swaps /services/<en> ↔ /services/<fr> slugs.
const localeizePath = localizePath;

/* ===== Menu links (stored once, English side) =====
   "/about" → a page (localized for French) · "/#services" → scrolls to a homepage
   section · https://, tel:, mailto: → a plain link · none → just a title */
const linkType = (link) =>
  !link ? "none" : /^\/#./.test(link) ? "scroll" : /^\/(?!\/)/.test(link) ? "page" : "external";

/** The menu in the page language; links / the "call" item come from the English side. */
function menuFrom(g, en) {
  const enItems = en.nav.items || [];
  return (g.nav.items || []).map((item, i) => {
    const enItem = enItems[i] || {};
    return {
      label: item.label,
      link: item.link || enItem.link,
      links: (item.links || []).map((sub, j) => {
        const enSub = enItem.links?.[j] || {};
        return { label: sub.label, link: sub.link || enSub.link, call: (sub.type || enSub.type) === "call" };
      }),
    };
  });
}

// Text inside an element that can't be the editable element itself (scroll links,
// buttons): plain text on the site, an editable <span> in the admin's on-page editor.
function EdText({ path, max, children }) {
  return useEd().editing ? <E path={path} max={max}>{children}</E> : children;
}

const Navbar = () => {
  const [openSidebar, setOpenSidebar] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [openLoginMenu, setOpenLoginMenu] = useState(false);
  const loginMenuRef = React.useRef(null);
  const { showNavbar, isMobile, isInHero } = useNavbar();
  const { dark, toggle: toggleDark } = useDarkMode();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { g, en } = useGlobalContent();
  const ed = useEd();
  const menu = menuFrom(g, en);

  // Language state (EN/FR) — derive from URL during SSR/prerender for a
  // deterministic initial state, then reconcile with localStorage/cookie
  // after hydration to honour user preference.
  const [lang, setLang] = useState(() => getLangFromPath(location.pathname));

  useEffect(() => {
    // Skip localStorage/cookie read during prerender so prerendered HTML
    // matches client's first render exactly (avoids React #418/#423).
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("HeadlessChrome")) return;
    const saved = localStorage.getItem("preferredLanguage") || Cookies.get("language");
    if (saved && saved !== lang) setLang(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep i18n + local state synced to the current URL
  useEffect(() => {
    const urlLang = getLangFromPath(location.pathname);
    if (i18n.language !== urlLang) i18n.changeLanguage(urlLang);
    if (lang !== urlLang) setLang(urlLang);
    
    // Close mobile menu when route changes
    setOpenSidebar(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLanguage = (code) => {
    setLang(code);
    i18n.changeLanguage(code);

    // Store the selected language in both localStorage and cookie
    localStorage.setItem("preferredLanguage", code);
    Cookies.set("language", code, { expires: 7 });

    // Rewrite current URL to include/remove /fr (and swap service slugs) while preserving search/hash
    const { pathname, search, hash } = location;
    const safePath = (pathname || "/").replace(/\/{2,}/g, "/");
    const nextPath = localizePath(code, safePath);

    navigate(`${nextPath}${search || ""}${hash || ""}`);
  };

  // Close the login portal menu on outside click
  useEffect(() => {
    if (!openLoginMenu) return;
    const onDocClick = (e) => {
      if (loginMenuRef.current && !loginMenuRef.current.contains(e.target)) {
        setOpenLoginMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openLoginMenu]);

  // Lock body scroll when mobile menu is open
  // (iOS Safari ignores overflow:hidden, so pin the body and restore the scroll position)
  useEffect(() => {
    if (!openSidebar) return;
    const { body, documentElement: html } = document;
    const y = window.scrollY;
    html.classList.add("mm-lock");
    body.classList.add("mm-lock");
    Object.assign(body.style, { position: "fixed", top: `-${y}px`, left: "0", right: "0", width: "100%" });
    return () => {
      html.classList.remove("mm-lock");
      body.classList.remove("mm-lock");
      Object.assign(body.style, { position: "", top: "", left: "", right: "", width: "" });
      // Leaving via a link navigates (and ScrollToTop handles it); otherwise go back to where we were
      window.scrollTo(0, y);
    };
  }, [openSidebar]);

  // Admin on-page editor: the menu title / dropdown link at this position (no-ops on the site)
  const titleTextEd = (idx) => ({ path: ["nav", "items", idx, "label"], max: 40 });
  const titleLinkEd = (idx) => ed.link(["nav", "items", idx, "link"], { shared: true });
  const subTextEd = (idx, i) => ({ path: ["nav", "items", idx, "links", i, "label"], max: 80 });
  const subLinkEd = (idx, i) => ed.link(["nav", "items", idx, "links", i, "link"], { shared: true });
  const subItemEd = (idx, i) => ed.item(["nav", "items", idx, "links"], i, { name: "link" });
  // Following a menu link closes the mobile menu (kept open while editing in the admin)
  const closeMenu = () => {
    if (!ed.editing) setOpenSidebar(false);
  };

  const renderLink = (item, idx, className) => {
    const type = linkType(item.link);
    if (type === "page") {
      return (
        <E as={RouterLink} className={className} to={localeizePath(lang, item.link)} role="menuitem" {...titleTextEd(idx)} {...titleLinkEd(idx)}>
          {item.label}
        </E>
      );
    }
    if (type === "external") {
      return (
        <E as="a" className={className} href={item.link} role="menuitem" {...titleTextEd(idx)} {...titleLinkEd(idx)}>
          {item.label}
        </E>
      );
    }
    if (type === "scroll") {
      return (
        <ScrollLink
          to={item.link.slice(2)}
          className={className}
          smooth={true}
          offset={-150}
          role="menuitem"
          {...titleLinkEd(idx)}
        >
          <EdText {...titleTextEd(idx)}>{item.label}</EdText>
        </ScrollLink>
      );
    }
    return (
      <E as="span" className={className} role="menuitem" {...titleTextEd(idx)} {...titleLinkEd(idx)}>
        {item.label}
      </E>
    );
  };

  return (
    <>
      <Sidebar openSidebar={openSidebar} onClose={() => setOpenSidebar(false)} />
      <PhoneModal isOpen={isPhoneModalOpen} onClose={() => setIsPhoneModalOpen(false)} />

      {/* Mobile keeps one stable dark bar; the desktop "drop" styling hid the
          theme/language/menu buttons when it leaked onto mobile. */}
      <nav className={`flex__center navbar ${
        showNavbar && !isMobile ? "drop" : ""
      } ${
        isMobile && !isInHero ? "mobile-scrolled" : ""
      }`}>
        {/* Logo (same markup as components/Logo; replaceable in the admin) */}
        <div className="flex__center logo__container">
          <RouterLink to="/" className="logo__link" aria-label="Go to homepage">
            <img src={en.nav.logo || navLogo} alt="Vision Security" width={180} height={48} decoding="async" {...ed.img(["nav", "logo"], { shared: true })} />
          </RouterLink>
        </div>

        {/* Desktop links */}
        <div className="navbar__links" role="menubar" aria-label={t("nav.primary", { defaultValue: "Primary" })}>
          {menu.map((item, idx) => (
            <div className="nav__group" key={idx} {...ed.item(["nav", "items"], idx, { name: "menu" })}>
              {renderLink(item, idx, "nav__item")}
              {item.links.length > 0 && (
                <div className="nav__dropdown" role="menu" aria-label={item.label}>
                  {item.links.map((sub, i) => {
                    // The "call" item opens the phone-numbers pop-up (not while editing in the admin)
                    if (sub.call) {
                      return (
                        <button
                          key={i}
                          className="nav__dropdown-link"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!ed.editing) setIsPhoneModalOpen(true);
                          }}
                          role="menuitem"
                          type="button"
                          {...subItemEd(idx, i)}
                        >
                          <EdText {...subTextEd(idx, i)}>{sub.label}</EdText>
                        </button>
                      );
                    }

                    const type = linkType(sub.link);
                    return type === "page" ? (
                      <E
                        as={RouterLink}
                        key={i}
                        to={localeizePath(lang, sub.link)}
                        className="nav__dropdown-link"
                        role="menuitem"
                        {...subTextEd(idx, i)}
                        {...subLinkEd(idx, i)}
                        {...subItemEd(idx, i)}
                      >
                        {sub.label}
                      </E>
                    ) : type === "external" ? (
                      <E as="a" key={i} className="nav__dropdown-link" href={sub.link} role="menuitem" {...subTextEd(idx, i)} {...subLinkEd(idx, i)} {...subItemEd(idx, i)}>
                        {sub.label}
                      </E>
                    ) : (
                      <ScrollLink
                        key={i}
                        to={type === "scroll" ? sub.link.slice(2) : undefined}
                        className="nav__dropdown-link"
                        smooth={true}
                        offset={-150}
                        role="menuitem"
                        {...subLinkEd(idx, i)}
                        {...subItemEd(idx, i)}
                      >
                        <EdText {...subTextEd(idx, i)}>{sub.label}</EdText>
                      </ScrollLink>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right-side controls */}
        <div className="flex__center">
          {/* Portal login (desktop only) */}
          <div className="navbar__login" ref={loginMenuRef}>
            <button
              type="button"
              className="dark-toggle navbar__login-btn"
              aria-haspopup="menu"
              aria-expanded={openLoginMenu}
              aria-label="Portal login"
              title="Login"
              onClick={() => setOpenLoginMenu((v) => !v)}
            >
              <LogIn size={16} />
            </button>
            {openLoginMenu && (
              <div className="nav__dropdown navbar__login-dropdown" role="menu" aria-label="Login">
                <a
                  href={EMPLOYEE_PORTAL_URL}
                  className="nav__dropdown-link"
                  role="menuitem"
                  onClick={() => setOpenLoginMenu(false)}
                >
                  <Briefcase size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                  Employee Portal
                </a>
                <a
                  href={CLIENT_PORTAL_URL}
                  className="nav__dropdown-link"
                  role="menuitem"
                  onClick={() => setOpenLoginMenu(false)}
                >
                  <UserRound size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                  Client Portal
                </a>
              </div>
            )}
          </div>

          {/* Admin login (desktop only) */}
          <RouterLink
            to="/admin"
            className="dark-toggle navbar__admin-btn"
            aria-label="Admin login"
            title="Admin"
          >
            <Lock size={16} />
          </RouterLink>

          {/* Dark mode toggle (desktop) */}
          <button
            type="button"
            className="dark-toggle"
            onClick={toggleDark}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Language switch (desktop) */}
          <div className="lang-switch" role="group" aria-label={t("lang.switch", { defaultValue: "Language" })}>
            <button
              type="button"
              className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
            <button
              type="button"
              className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
              onClick={() => setLanguage("fr")}
            >
              FR
            </button>
          </div>

          <button
            className="flex__center icon menu__btn"
            onClick={() => setOpenSidebar(true)}
            type="button"
          >
            <Menu 
              color={isMobile && showNavbar && !isInHero && !dark ? "#3244ba" : "#fff"} 
              size={28} 
            />
          </button>
        </div>
      </nav>

      {/* Mobile slide-in menu */}
      {openSidebar && (
        <div className="mobileMenu" role="dialog" aria-modal="true">
          <div className="mobileMenu__header">
            <span className="mobileMenu__title">{t("menu.title", { defaultValue: "Menu" })}</span>

            {/* Language switch (mobile) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                className="dark-toggle dark-toggle--mobile"
                onClick={toggleDark}
                aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
              >
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <div className="lang-switch lang-switch--mobile" role="group">
              <button
                type="button"
                className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
                onClick={() => setLanguage("en")}
              >
                EN
              </button>
              <button
                type="button"
                className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
                onClick={() => setLanguage("fr")}
              >
                FR
              </button>
            </div>
            </div>
          </div>

          <button
            className="mobileMenu__close"
            onClick={() => setOpenSidebar(false)}
            type="button"
          >
            <X size={32} />
          </button>

          <nav className="mobileMenu__nav" role="menu">
            {menu.map((item, idx) => {
              const hasSub = item.links.length > 0;
              const type = linkType(item.link);
              return (
                <details
                  className="mobileMenu__item"
                  key={idx}
                  onToggle={(e) => {
                    // One section open at a time
                    if (!e.currentTarget.open) return;
                    e.currentTarget.parentElement
                      .querySelectorAll(".mobileMenu__item[open]")
                      .forEach((d) => d !== e.currentTarget && (d.open = false));
                  }}
                  {...ed.item(["nav", "items"], idx, { name: "menu" })}
                >
                  <summary className="mobileMenu__summary">
                    <span className="mobileMenu__label">
                      {/* With a submenu the whole row just opens it — a link here would
                          close the menu (or scroll the homepage) instead */}
                      {hasSub ? (
                        <E as="span" className="mobileMenu__link" {...titleTextEd(idx)}>{item.label}</E>
                      ) : type === "page" ? (
                        <E
                          as={RouterLink}
                          to={localeizePath(lang, item.link)}
                          className="mobileMenu__link"
                          role="menuitem"
                          onClick={closeMenu}
                          {...titleTextEd(idx)}
                          {...titleLinkEd(idx)}
                        >
                          {item.label}
                        </E>
                      ) : type === "external" ? (
                        <E
                          as="a"
                          href={item.link}
                          className="mobileMenu__link"
                          role="menuitem"
                          onClick={closeMenu}
                          {...titleTextEd(idx)}
                          {...titleLinkEd(idx)}
                        >
                          {item.label}
                        </E>
                      ) : type === "scroll" ? (
                        <ScrollLink
                          to={item.link.slice(2)}
                          className="mobileMenu__link"
                          smooth
                          offset={-150}
                          role="menuitem"
                          onClick={closeMenu}
                          {...titleLinkEd(idx)}
                        >
                          <EdText {...titleTextEd(idx)}>{item.label}</EdText>
                        </ScrollLink>
                      ) : (
                        <E as="span" className="mobileMenu__link" {...titleTextEd(idx)} {...titleLinkEd(idx)}>{item.label}</E>
                      )}
                    </span>

                    {/* visual toggle indicator */}
                    {hasSub && <span className="mobileMenu__toggle" aria-hidden />}
                  </summary>

                  {hasSub && (
                    <div className="mobileMenu__submenu" role="group" aria-label={item.label}>
                      {item.links.map((sub, i) => {
                        // The "call" item opens the phone-numbers pop-up (not while editing in the admin)
                        if (sub.call) {
                          return (
                            <button
                              key={i}
                              className="mobileMenu__sublink"
                              onClick={(e) => {
                                e.preventDefault();
                                if (ed.editing) return;
                                setIsPhoneModalOpen(true);
                                setOpenSidebar(false);
                              }}
                              role="menuitem"
                              type="button"
                              {...subItemEd(idx, i)}
                            >
                              <EdText {...subTextEd(idx, i)}>{sub.label}</EdText>
                            </button>
                          );
                        }

                        const subType = linkType(sub.link);
                        if (subType === "page") {
                          return (
                            <E
                              as={RouterLink}
                              key={i}
                              to={localeizePath(lang, sub.link)}
                              className="mobileMenu__sublink"
                              role="menuitem"
                              onClick={closeMenu}
                              {...subTextEd(idx, i)}
                              {...subLinkEd(idx, i)}
                              {...subItemEd(idx, i)}
                            >
                              {sub.label}
                            </E>
                          );
                        }
                        if (subType === "external") {
                          return (
                            <E
                              as="a"
                              key={i}
                              href={sub.link}
                              className="mobileMenu__sublink"
                              role="menuitem"
                              onClick={closeMenu}
                              {...subTextEd(idx, i)}
                              {...subLinkEd(idx, i)}
                              {...subItemEd(idx, i)}
                            >
                              {sub.label}
                            </E>
                          );
                        }
                        return (
                          <ScrollLink
                            key={i}
                            to={subType === "scroll" ? sub.link.slice(2) : undefined}
                            className="mobileMenu__sublink"
                            smooth
                            offset={-150}
                            role="menuitem"
                            onClick={closeMenu}
                            {...subLinkEd(idx, i)}
                            {...subItemEd(idx, i)}
                          >
                            <EdText {...subTextEd(idx, i)}>{sub.label}</EdText>
                          </ScrollLink>
                        );
                      })}
                    </div>
                  )}
                </details>
              );
            })}
          </nav>

          {/* Portal login links (mobile only) */}
          <a
            href={EMPLOYEE_PORTAL_URL}
            className="mobileMenu__admin-link"
            onClick={() => setOpenSidebar(false)}
          >
            <Briefcase size={15} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Employee Portal
          </a>
          <a
            href={CLIENT_PORTAL_URL}
            className="mobileMenu__admin-link"
            onClick={() => setOpenSidebar(false)}
          >
            <UserRound size={15} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Client Portal
          </a>

          {/* Admin link (mobile only) */}
          <RouterLink
            to="/admin"
            className="mobileMenu__admin-link"
            onClick={() => setOpenSidebar(false)}
          >
            <Lock size={15} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Admin Dashboard
          </RouterLink>
        </div>
      )}
    </>
  );
};

export default Navbar;